/**
 * First-play interactive prologue (LOCKED-DESIGN 12.2 / 18.6). A do-it-to-
 * advance sequence in the deadpan-facility / warm-coach playful-industrial
 * voice (18.2). No score clock, no overflow — warm-up trash drips in slowly
 * while the player learns each verb in the locked order:
 *
 *   move → gravity beam → the SEALED glass beat → place a blue anchor (a real
 *   choice) → pick an orange receiver (a real choice) → one clean load →
 *   optional traversal → a graduation ready-beat.
 *
 * The glass beat is mechanically enforced: portals stay concealed and the step
 * only advances on ACTUAL reinforced-bulkhead contact (Engine.reportBulkheadHit),
 * never on any release in any direction. Owns its own DOM overlay; the Engine
 * feeds it progress events and drives the on-world cue markers.
 * Skippable anytime (button, Esc, or pad Start).
 */

import { TUTORIAL_CARD_RECT } from "../world/ArenaReadability.js";
import { cssHex, PALETTE, RECEIVER_TIER_COLORS } from "../render/theme.js";
import { orientationRelief } from "./TutorialTiming.js";

export type TutorialStepId =
  | "move"
  | "vacuum"
  | "glass"
  | "blue"
  | "orange"
  | "clean"
  | "chase"
  | "traverse"
  | "ready";

/** Which orientation script runs: the ranked adult prologue or the kid one. */
export type TutorialMode = "standard" | "simple";

/** Big token-colored FORM glyph for the Simple card (LOCKED-DESIGN 18.11). */
type SimpleGlyph = "move" | "grab" | "ring" | "chase" | "star";

interface StepDef {
  id: TutorialStepId;
  title: string;
  subKeyboard: string;
  subPad: string;
  /** Simple-mode only: the ~64px card glyph + its signal color. */
  glyph?: SimpleGlyph;
  glyphColor?: number;
  /**
   * Softened sub-lines (LOCKED-DESIGN 18.15 / F9). After SOFTEN_AFTER_S on a
   * stuck Simple throw beat the card swaps to this friendlier, more concrete
   * instruction. Only the Simple `clean`/`chase` beats carry them.
   */
  softKeyboard?: string;
  softPad?: string;
}

/** Cue marker request (screen-space). QA keys on data-cue-target, never copy. */
export interface CueSpec {
  x: number;
  y: number;
  label: string;
  color: string;
  /** stable authored-target name (e.g. "floor", "safe", "glass") */
  target: string;
  /** false = a dimmer alternative in a real choice */
  primary?: boolean;
  /**
   * Ring size multiplier (LOCKED-DESIGN 18.15 / F9). The softened Simple throw
   * beat re-shows a BIGGER "THROW HERE" ring. Default 1.
   */
  scale?: number;
  /**
   * Gentle attention pulse. FLASH-REDUCTION GATED — the Engine passes false when
   * flash reduction is on, and the Tutorial additionally refuses it under
   * prefers-reduced-motion, so the softened cue is never a pulsing cue for a
   * player who asked for calm. Default false.
   */
  pulse?: boolean;
}

const STEPS: StepDef[] = [
  {
    id: "move",
    title: "WELCOME TO YOUR SHIFT, OPERATOR",
    subKeyboard: "Find your footing — roll around on WASD.",
    subPad: "Find your footing — roll around on the left stick.",
  },
  {
    id: "vacuum",
    title: "GRAVITY BEAM · INTAKE TEST",
    subKeyboard: "Hold E (or left-click) and reel in a couple of loose pieces.",
    subPad: "Hold Ⓧ (or RT) and reel in a couple of loose pieces.",
  },
  {
    id: "glass",
    title: "NOTICE · THE CHAMBER IS SEALED",
    subKeyboard: "Lob your load straight at the reinforced glass. Go on — try it.",
    subPad: "Lob your load straight at the reinforced glass. Go on — try it.",
  },
  {
    id: "blue",
    title: "TRANSFER SYSTEM · PLACE A BLUE ANCHOR",
    subKeyboard: "That's the way in. Aim at a lit blue panel — your pick — and press Q.",
    subPad: "That's the way in. Aim at a lit blue panel — your pick — and press LB.",
  },
  {
    id: "orange",
    title: "TRANSFER SYSTEM · PICK A RECEIVER",
    subKeyboard: "Now the exit. Aim through the glass at an orange receiver and press R.",
    subPad: "Now the exit. Aim through the glass at an orange receiver and press RB.",
  },
  {
    id: "clean",
    title: "RUN ONE CLEAN LOAD",
    subKeyboard: "Scoop a load and release it into BLUE. Momentum does the rest.",
    subPad: "Scoop a load and release it into BLUE. Momentum does the rest.",
  },
  {
    id: "traverse",
    title: "OPTIONAL · RIDE THE BEAM YOURSELF",
    // The verb is JUMP since 18.25: the intent gate only passes a player who is
    // airborne or dashing INTO the chamber, so "step through" now describes the
    // one approach that is refused. Walking back out is still free, which is why
    // only the entry is named here. This beat is standard-only (Simple omits it).
    subKeyboard: "Feeling brave? Jump into a portal to scope the recovery deck.",
    subPad: "Feeling brave? Jump (A) into a portal to scope the recovery deck.",
  },
  {
    id: "ready",
    title: "ORIENTATION COMPLETE",
    subKeyboard: "Nice work. Place your portals — your shift starts on the countdown.",
    subPad: "Nice work. Place your portals — your shift starts on the countdown.",
  },
];

/**
 * Simple-mode orientation (LOCKED-DESIGN 18.11 v2): five early-reader beats —
 * move, grab, throw through the pre-placed blue portal, CHASE the moving ring,
 * celebrate. The glass / blue / orange / traverse beats are omitted; portals are
 * pre-placed before step 1 so the kid never touches Q/R. Existing progress
 * events drive advance (reportMove, reportHeldCount, reportIncinerated, the
 * ready timer); the new "chase" beat is entered on the first clean throw and
 * cleared on the first incineration AFTER the engine's scripted ring move — the
 * reportRingMoved gate makes that ordering real, not just aspirational.
 */
const SIMPLE_STEPS: StepDef[] = [
  {
    id: "move",
    title: "MOVE!",
    subKeyboard: "Roll with WASD.",
    subPad: "Roll with the stick.",
    glyph: "move",
    glyphColor: PALETTE.avatar,
  },
  {
    id: "vacuum",
    title: "GRAB TRASH!",
    subKeyboard: "Hold E. Pull trash in.",
    subPad: "Hold Ⓧ. Pull trash in.",
    glyph: "grab",
    glyphColor: PALETTE.conveyance,
  },
  {
    id: "clean",
    title: "THROW IT IN!",
    subKeyboard: "Let go at the blue ring.",
    subPad: "Let go at the blue ring.",
    softKeyboard: "Get right next to the ring and let go!",
    softPad: "Get right next to the ring and let go!",
    glyph: "ring",
    glyphColor: PALETTE.anchorBlue,
  },
  {
    id: "chase",
    title: "FOLLOW THE RING!",
    subKeyboard: "The ring moves. Chase it!",
    subPad: "The ring moves. Chase it!",
    softKeyboard: "Get right next to the ring and let go!",
    softPad: "Get right next to the ring and let go!",
    glyph: "chase",
    glyphColor: PALETTE.anchorBlue,
  },
  {
    id: "ready",
    title: "YOU DID IT!",
    subKeyboard: "Trash is coming. Chase the ring!",
    subPad: "Trash is coming. Chase the ring!",
    glyph: "star",
    glyphColor: RECEIVER_TIER_COLORS.safe,
  },
];

/** Inline SVG for a Simple card glyph, stroked/filled in currentColor. */
function simpleGlyphSvg(glyph: SimpleGlyph): string {
  const open = `<svg viewBox="0 0 64 64" width="64" height="64" fill="none" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">`;
  if (glyph === "move") {
    // arrow cross — four arrows from the center
    return `${open}<path d="M32 10 V54 M10 32 H54"/><path d="M32 8 l-6 8 M32 8 l6 8 M32 56 l-6 -8 M32 56 l6 8 M8 32 l8 -6 M8 32 l8 6 M56 32 l-8 -6 M56 32 l-8 6"/></svg>`;
  }
  if (glyph === "grab") {
    // gravity beam — converging chevrons pulling into a core dot
    return `${open}<circle cx="32" cy="32" r="4" fill="currentColor" stroke="none"/><path d="M14 14 l8 3 3 -8 M50 14 l-8 3 -3 -8 M14 50 l8 -3 3 8 M50 50 l-8 -3 -3 8"/></svg>`;
  }
  if (glyph === "ring") {
    // blue ring — the transfer portal
    return `${open}<circle cx="32" cy="32" r="20"/><circle cx="32" cy="32" r="9"/></svg>`;
  }
  if (glyph === "chase") {
    // moving ring — a ring with motion arrows (18.11 v2 chase beat)
    return `${open}<circle cx="32" cy="32" r="15"/><path d="M52 24 h8 M52 24 l-4 -4 M52 24 l-4 4 M12 40 h-8 M12 40 l4 -4 M12 40 l4 4"/></svg>`;
  }
  // star — celebration
  return `${open}<path d="M32 8 l6.5 15 16.5 1.5 -12.6 10.7 4 16.1 -14.4 -8.7 -14.4 8.7 4 -16.1 -12.6 -10.7 16.5 -1.5 Z" fill="currentColor" stroke="none"/></svg>`;
}

/** Base on-world cue ring diameter; the F9 softened cue scales this up. */
const CUE_RING_PX = 76;
const OPTIONAL_LINGER = 4; // seconds the optional traversal beat waits
const GRADUATION_LINGER = 1.4; // ready-beat card time before handing off to the run
/** Simple's celebrate beat lingers a touch longer before the run countdown. */
const SIMPLE_READY_LINGER = 2;

export class Tutorial {
  active = true;
  private stepIndex = 0;

  // progress trackers
  private movedDistance = 0;
  private optionalTimer = OPTIONAL_LINGER;
  private readyTimer = GRADUATION_LINGER;
  /** Simple "chase" beat (18.11 v2): true once the scripted ring move fired. */
  private ringMoved = false;
  /** Seconds the CURRENT beat has been on screen (18.15 / F9). Reset on advance. */
  private stepElapsed = 0;
  /** True once the current beat softened (18.15 / F9). Reset on advance. */
  private softenedNow = false;
  private softenCallback: (() => void) | null = null;
  private reliefAdvanceCallback: (() => void) | null = null;

  private readonly root: HTMLDivElement;
  private readonly glyphEl: HTMLDivElement;
  private readonly titleEl: HTMLDivElement;
  private readonly subEl: HTMLDivElement;
  private readonly dotsEl: HTMLDivElement;
  private readonly cueLayer: HTMLDivElement;
  private readonly cuePool: Array<{ ring: HTMLDivElement; label: HTMLDivElement; pulse: Animation | null }> = [];
  private padMode = false;
  private doneCallback: ((skipped: boolean) => void) | null = null;
  private stepCallback: (() => void) | null = null;
  /** Selected orientation script (LOCKED-DESIGN 18.11 mode-aware step table). */
  private readonly steps: StepDef[];
  private readonly simple: boolean;

  constructor(container: HTMLElement, mode: TutorialMode = "standard") {
    this.simple = mode === "simple";
    this.steps = this.simple ? SIMPLE_STEPS : STEPS;
    if (this.simple) this.readyTimer = SIMPLE_READY_LINGER;

    const root = document.createElement("div");
    root.dataset.tutorial = "true";
    root.dataset.tutorialMode = mode;
    root.style.cssText =
      "position:absolute;inset:0;z-index:6;pointer-events:none;user-select:none;" +
      "font:13px/1.5 ui-monospace,monospace;";

    const card = document.createElement("div");
    card.dataset.tutorialCard = "true";
    card.dataset.hudElement = "tutorial-card";
    card.style.cssText =
      `position:absolute;top:${TUTORIAL_CARD_RECT.top}px;left:${TUTORIAL_CARD_RECT.left}px;` +
      `width:min(${TUTORIAL_CARD_RECT.right - TUTORIAL_CARD_RECT.left}px,calc(100% - 92px));text-align:left;` +
      "padding:14px 18px;background:rgba(8,10,16,0.88);border:1px solid #3bc9a866;" +
      "border-radius:10px;backdrop-filter:blur(4px);transform-origin:top left;";
    // Simple card: a big token-colored FORM glyph slot above the copy, and the
    // early-reader copy set ~1.3× (LOCKED-DESIGN 18.11). Standard keeps the slot
    // empty/hidden so the adult prologue card is byte-identical.
    this.glyphEl = document.createElement("div");
    this.glyphEl.dataset.tutorialGlyph = "true";
    this.glyphEl.style.cssText = this.simple
      ? "width:64px;height:64px;margin-bottom:6px;"
      : "display:none;";
    this.titleEl = document.createElement("div");
    this.titleEl.style.cssText = this.simple
      ? "font-size:22px;font-weight:800;letter-spacing:.5px;color:#eef2f8;text-shadow:0 0 10px #3bc9a855;"
      : "font-size:16px;font-weight:800;letter-spacing:.5px;color:#eef2f8;text-shadow:0 0 10px #3bc9a855;";
    this.subEl = document.createElement("div");
    this.subEl.style.cssText = this.simple
      ? "margin-top:6px;font-size:18px;color:#3bc9a8;"
      : "margin-top:5px;font-size:14px;color:#3bc9a8;";
    this.dotsEl = document.createElement("div");
    this.dotsEl.style.cssText = "margin-top:8px;font-size:11px;letter-spacing:6px;color:#5d6675;";
    card.append(this.glyphEl, this.titleEl, this.subEl, this.dotsEl);

    // cue markers live in their own layer so a real choice can highlight
    // several candidate anchors/receivers at once (LOCKED-DESIGN 18.6).
    this.cueLayer = document.createElement("div");
    this.cueLayer.style.cssText = "position:absolute;inset:0;pointer-events:none;";

    const skip = document.createElement("button");
    skip.textContent = "SKIP ▸";
    skip.dataset.testid = "tutorial-skip";
    skip.dataset.hudElement = "tutorial-skip";
    // width:auto overrides the shell's global `button { width: 100% }`, which
    // stretched this into a full-width strip on top of the keybind hint bar.
    skip.style.cssText =
      "position:absolute;bottom:14px;right:16px;width:auto;pointer-events:auto;cursor:pointer;" +
      "font:12px ui-monospace,monospace;color:#8b95a7;background:rgba(8,10,16,0.6);" +
      "border:1px solid #3a4150;border-radius:6px;padding:5px 12px;";
    skip.onclick = () => this.finish(true);

    root.append(card, this.cueLayer, skip);
    container.appendChild(root);
    this.root = root;
    this.renderStep(true);
  }

  onDone(cb: (skipped: boolean) => void): void {
    this.doneCallback = cb;
  }

  /** fires on every step advance (for the happy chime) */
  onStep(cb: () => void): void {
    this.stepCallback = cb;
  }

  /**
   * Fires ONCE per beat, on the softening edge (LOCKED-DESIGN 18.15 / F9). The
   * Engine uses it to re-show the big "THROW HERE" ring; the per-frame reads
   * (assist widening, cue size) use the `softened` getter instead.
   */
  onSoften(cb: () => void): void {
    this.softenCallback = cb;
  }

  /** Fires just before a timed relief advance, so the Engine can toast warmly. */
  onReliefAdvance(cb: () => void): void {
    this.reliefAdvanceCallback = cb;
  }

  /** True while the current beat is softened (18.15 / F9). Simple-only. */
  get softened(): boolean {
    return this.softenedNow;
  }

  /** Seconds the current beat has been on screen — QA reads via __tpz.tutorial. */
  get stepSeconds(): number {
    return this.stepElapsed;
  }

  /**
   * QA hook (window.__tpz.tutorial.debugElapse): advance the relief clock without
   * waiting in real time. The Playwright config's 20 s timeout is shorter than the
   * 22/35 s thresholds, and `Engine.debugAdvance` refuses to run while the
   * tutorial is active, so this is the only way to drive the F9 beats headlessly.
   * Deliberately routed through the SAME `update` path as a real frame so the test
   * exercises production logic, not a shortcut.
   */
  debugElapse(seconds: number): void {
    this.update(seconds);
  }

  currentStep(): TutorialStepId {
    return this.steps[this.stepIndex]?.id ?? "ready";
  }

  /**
   * Paint the on-world cue markers for the current beat. Each marker carries a
   * stable data-cue-target so QA can align it without matching copy; a real
   * choice passes several candidates and the extras render dimmer.
   */
  showCues(cues: CueSpec[]): void {
    while (this.cuePool.length < cues.length) this.cuePool.push(this.makeCue());
    for (let i = 0; i < this.cuePool.length; i++) {
      const marker = this.cuePool[i]!;
      const cue = cues[i];
      if (!cue) {
        marker.ring.style.display = "none";
        marker.ring.dataset.portalCue = "";
        marker.ring.dataset.cueTarget = "";
        continue;
      }
      marker.ring.style.display = "block";
      marker.ring.style.left = `${Math.round(cue.x)}px`;
      marker.ring.style.top = `${Math.round(cue.y)}px`;
      marker.ring.style.borderColor = cue.color;
      marker.ring.style.color = cue.color;
      marker.ring.style.opacity = cue.primary === false ? "0.66" : "1";
      // Softened Simple throw beat (18.15 / F9): a bigger ring, and a pulse ONLY
      // when the player has not asked for reduced flashes/motion.
      const scale = cue.scale ?? 1;
      const size = Math.round(CUE_RING_PX * scale);
      marker.ring.style.width = `${size}px`;
      marker.ring.style.height = `${size}px`;
      marker.ring.dataset.cueScale = scale === 1 ? "" : scale.toFixed(2);
      this.setCuePulse(marker, cue.pulse === true);
      marker.ring.dataset.portalCue = cue.label.toLowerCase();
      marker.ring.dataset.cueTarget = cue.target;
      marker.label.textContent = cue.label;
    }
  }

  /**
   * Attention pulse for a cue ring, via the Web Animations API so no global
   * keyframes are injected. Refused outright under prefers-reduced-motion — the
   * caller already gates on the in-game flash-reduction setting, and this is the
   * OS-level belt to that braces (18.15 / F9 "softening respects flash reduction").
   */
  private setCuePulse(marker: { ring: HTMLDivElement; pulse: Animation | null }, on: boolean): void {
    const allowed = on && !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!allowed) {
      marker.pulse?.cancel();
      marker.pulse = null;
      marker.ring.dataset.cuePulse = "";
      return;
    }
    if (marker.pulse) return;
    marker.ring.dataset.cuePulse = "on";
    marker.pulse = marker.ring.animate?.(
      [{ opacity: 1 }, { opacity: 0.55 }, { opacity: 1 }],
      { duration: 1100, iterations: Infinity },
    ) ?? null;
  }

  clearCues(): void {
    for (const marker of this.cuePool) {
      this.setCuePulse(marker, false);
      marker.ring.style.display = "none";
      marker.ring.dataset.portalCue = "";
      marker.ring.dataset.cueTarget = "";
    }
  }

  private makeCue(): { ring: HTMLDivElement; label: HTMLDivElement; pulse: Animation | null } {
    const ring = document.createElement("div");
    ring.dataset.portalCue = "";
    ring.style.cssText =
      `display:none;position:absolute;width:${CUE_RING_PX}px;height:${CUE_RING_PX}px;transform:translate(-50%,-50%);` +
      "border:3px solid #35b8ff;border-radius:50%;box-shadow:0 0 18px currentColor,inset 0 0 14px currentColor;";
    const label = document.createElement("div");
    label.style.cssText =
      "position:absolute;left:50%;top:calc(100% + 7px);transform:translateX(-50%);white-space:nowrap;" +
      "padding:4px 7px;border-radius:5px;background:#070a10e8;color:#f4f7fb;font-weight:800;font-size:11px;";
    ring.append(label);
    this.cueLayer.append(ring);
    return { ring, label, pulse: null };
  }

  setPadMode(pad: boolean): void {
    if (pad === this.padMode) return;
    this.padMode = pad;
    this.renderStep(false);
  }

  // ---- progress events from the engine ----

  reportMove(distance: number): void {
    this.movedDistance += distance;
    if (this.currentStep() === "move" && this.movedDistance > 6) this.advance();
  }

  reportHeldCount(count: number): void {
    // Simple lowers the grab threshold to a single piece (18.11) — a kid should
    // graduate the beat as soon as the beam actually pulls something in.
    if (this.currentStep() === "vacuum" && count >= (this.simple ? 1 : 2)) this.advance();
  }

  /**
   * The enforced glass beat (LOCKED-DESIGN 18.6): the ONLY thing that clears
   * this step is a thrown piece actually striking the reinforced bulkhead —
   * a stray release in another direction never counts.
   */
  reportBulkheadHit(): void {
    if (this.currentStep() === "glass") this.advance();
  }

  /**
   * The engine's scripted orientation ring move fired (LOCKED-DESIGN 18.11 v2).
   * The "chase" beat only graduates on an incineration AFTER this, so a kid can
   * never skip past the one ring-move beat before ever seeing the ring move.
   */
  reportRingMoved(): void {
    this.ringMoved = true;
  }

  reportIncinerated(): void {
    // "clean" advances to the chase beat on the first incineration; "chase"
    // advances to "ready" only on an incineration AFTER the scripted ring move
    // (reportRingMoved). The ringMoved gate is load-bearing: Simple's beam
    // routinely carries several crates per throw (6 bulk since 18.15), so
    // multiple pieces incinerate
    // within one physics pass — without the gate a second piece would advance
    // chase -> ready before the 8s telegraph ever relocates the ring.
    const step = this.currentStep();
    if (step === "clean") this.advance();
    else if (step === "chase" && this.ringMoved) this.advance();
  }

  reportPortalPlaced(endpoint: "blue" | "orange"): void {
    if (endpoint === "blue" && this.currentStep() === "blue") this.advance();
    if (endpoint === "orange" && this.currentStep() === "orange") this.advance();
  }

  reportTraversed(): void {
    if (this.currentStep() === "traverse") this.advance();
  }

  reportSkipPressed(): void {
    this.finish(true);
  }

  /**
   * per-frame; drives the optional-traversal wait, the graduation ready-beat and
   * (Simple only) the F9 orientation relief on the two throw beats.
   */
  update(dt: number): void {
    if (!this.active) return;
    this.stepElapsed += dt;
    if (this.currentStep() === "traverse") {
      this.optionalTimer -= dt;
      if (this.optionalTimer <= 0) this.advance();
    } else if (this.currentStep() === "ready") {
      this.readyTimer -= dt;
      if (this.readyTimer <= 0) this.finish(false);
    }
    this.applyRelief();
  }

  /**
   * Orientation relief (LOCKED-DESIGN 18.15 / F9). SIMPLE ONLY — the adult
   * prologue's beats all have their own enforced exits and are deliberately
   * untouched. The decision itself lives in the pure `orientationRelief` rule;
   * this only applies it.
   */
  private applyRelief(): void {
    if (!this.simple) return;
    const { soften, advance } = orientationRelief(this.currentStep(), this.stepElapsed, this.ringMoved);
    if (soften && !this.softenedNow) {
      this.softenedNow = true;
      this.renderStep(false); // swaps in the friendlier sub-line
      this.softenCallback?.();
    }
    // The guard mirrors advance()'s own: never announce a release that cannot
    // happen (clean/chase are never the last beat, but the two must not drift).
    if (advance && this.stepIndex < this.steps.length - 1) {
      // Warm release, never a failure framing: the Engine toasts "nice try".
      this.reliefAdvanceCallback?.();
      this.advance();
    }
  }

  private advance(): void {
    // Every beat starts its relief clock fresh, softened or not (18.15 / F9).
    this.stepElapsed = 0;
    this.softenedNow = false;
    if (this.stepIndex >= this.steps.length - 1) return;
    this.stepIndex++;
    this.stepCallback?.();
    this.renderStep(true);
  }

  private renderStep(pop: boolean): void {
    const step = this.steps[this.stepIndex];
    if (!step) return;
    this.titleEl.textContent = step.title;
    // Softened beats (18.15 / F9) swap to the concrete "get right next to the
    // ring" line; beats without a softened variant keep their normal sub-line.
    const soft = this.softenedNow ? (this.padMode ? step.softPad : step.softKeyboard) : undefined;
    this.subEl.textContent = soft ?? (this.padMode ? step.subPad : step.subKeyboard);
    this.dotsEl.textContent = this.steps.map((_, i) => (i <= this.stepIndex ? "●" : "○")).join("");
    if (this.simple && step.glyph) {
      this.glyphEl.style.display = "";
      this.glyphEl.style.color = cssHex(step.glyphColor ?? PALETTE.avatar);
      this.glyphEl.innerHTML = simpleGlyphSvg(step.glyph);
    }
    if (pop && !window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      const card = this.titleEl.parentElement!;
      card.style.transition = "none";
      card.style.transform = "scale(1.06)";
      requestAnimationFrame(() => {
        card.style.transition = "transform .2s cubic-bezier(.2,.8,.3,1)";
        card.style.transform = "scale(1)";
      });
    }
  }

  private finish(skipped: boolean): void {
    if (!this.active) return;
    this.active = false;
    this.root.remove();
    this.doneCallback?.(skipped);
  }

  destroy(): void {
    this.active = false;
    this.root.remove();
  }
}
